import React, { useRef, useState } from "react";
import Reveal from "../anim/Reveal";
import Icon from "../icons/Icon";
import Section from "../layout/Section";
import MicroLabel from "../ui/MicroLabel";
import Statement from "../ui/Statement";
import TicketCard from "../ui/TicketCard";
import { submitContactInquiry } from "../../services/contactService";
import { SITE_CONTENT } from "../../constants/siteContent";
import {
  whatsappUrl,
  mailUrl,
  telUrl,
  phoneDisplay,
  responseNote,
  EXTERNAL_LINK_PROPS
} from "../../utils/contact";
import styles from "./FaqLeadForm.module.css";

/* FaqLeadForm — the site's FAQ + lead capture, as an olive INSET PANEL.
 *
 * Left (on the olive): micro label, the mixed sans/serif heading, then the real
 * FAQ from SITE_CONTENT.faq as a hairline-ruled accordion — question rows in the
 * condensed display face — and the direct-contact block pinned to the bottom of
 * the column.
 * Right: the lead form on a paper TicketCard (a paper ticket on the olive panel),
 * wired to the existing contactService.
 * At <= 980px the two stack with the FORM FIRST (CSS `order`; the DOM keeps the
 * h2 ahead of the form's h3, so the heading outline stays correct).
 *
 * No notch on this panel — the footer directly below owns the notched one.
 *
 * Anti-spam is invisible: a honeypot field plus a minimum time-on-form. There is
 * no puzzle for a real person to solve.
 *
 * The form is not the only way through. Plenty of buyers will never fill one in
 * but will happily send a message, so WhatsApp sits beside the submit button as
 * a real alternative — a text link, not a second pill.
 */

// TODO(content): section label + heading. `accent` sets in the serif face.
const KICKER = "FAQ";
const HEADING = { lead: "Questions we get", accent: "asked", tail: "." };
const HEADING_ID = "faq-title";

// TODO(content): replace with real Appinfigo content
const BUDGETS = ["Under ₹5L", "₹5L – ₹15L", "₹15L – ₹40L", "₹40L+", "Not sure yet"];

// TODO(content): lead-form copy (FAQ copy itself comes from SITE_CONTENT.faq).
// The title must read on its own: on phones the form sits ABOVE the questions.
const FORM_COPY = {
  titleLead: "Tell us about your",
  titleAccent: "project",
  titleTail: ".",
  help: "We’ll reply within one business day.",
  submit: "Send project brief",
  sending: "Sending…",
  privacy: "Your details stay private and are only used to reply to you."
};

// TODO(content): field labels, hints and placeholders
const FIELDS = {
  name: "Name",
  company: "Company",
  email: "Work email",
  emailPlaceholder: "you@company.com",
  phone: "Phone",
  phonePlaceholder: "+91",
  budget: "Budget range",
  budgetPlaceholder: "Select a range",
  details: "Project brief",
  detailsPlaceholder: "What are you building, and what does done look like?",
  optional: "(optional)"
};

// TODO(content): direct-contact prompt shown under the FAQ list
const DIRECT_PROMPT = "Prefer to talk it through? Reach the team directly.";

/* The WhatsApp alternative next to the submit button. 22 characters, so it can
   never wrap to a second line at any breakpoint. */
const WHATSAPP_LABEL = "Message us on WhatsApp";

// TODO(content): the word between the submit pill and the WhatsApp link
const ALT_OR = "or";

/* Real FAQ content — shape: { title, subtitle, items: [{ question, answer }] } */
const FAQ_CONTENT = SITE_CONTENT.faq || {};
const FAQS = (FAQ_CONTENT.items || []).map((item) => ({
  q: item.question,
  a: item.answer
}));

/* Real contact details, read from SITE_CONTENT — never invented here. */
const CONTACT = SITE_CONTENT.contact || {};
const CONTACT_EMAIL = CONTACT.email || "";

/* Every direct route to the team is built by src/utils/contact.js — the one
   place that knows the number and the pre-filled openers, so this section can
   never drift from the header, the footer or the contact page.
   Each builder returns "" when that channel is not configured, and "" means
   render nothing at all. A dead link is worse than no link. */
const WA_BRIEF_URL = whatsappUrl("quote"); /* opener: "…I'd like a fixed-price quote" */
const WA_TALK_URL = whatsappUrl("call"); /* opener: "…I'd like to book a 30-minute call" */
const MAIL_URL = mailUrl("Project inquiry");
const TEL_URL = telUrl();
const PHONE_DISPLAY = phoneDisplay();
const RESPONSE_NOTE = responseNote();

/* Invisible anti-spam. A bot fills every field it can see (including the
   honeypot) and posts within milliseconds; a person does neither. */
const MIN_FILL_MS = 3000;

/* submitContactInquiry(formData, captchaToken) keeps its signature. This inline
   form deliberately ships no reCAPTCHA widget, so it sends a benign, non-empty
   sentinel — the same shape the previous build sent. The Cloud Function only
   reads this value when ENFORCE_RECAPTCHA is switched on server-side; the real
   spam gate for this form is the honeypot (also enforced server-side via the
   `website` field) plus the timing check below. */
const ANTISPAM_TOKEN = "honeypot-timing";

const EMPTY = { name: "", company: "", email: "", phone: "", budget: "", details: "", website: "" };

export default function FaqLeadForm() {
  const [form, setForm] = useState(EMPTY);
  const [openFaq, setOpenFaq] = useState(0);
  const [status, setStatus] = useState({ state: "idle", message: "" });
  const startedAt = useRef(Date.now());

  const update = (key) => (event) => {
    setForm((prev) => ({ ...prev, [key]: event.target.value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (status.state === "submitting") return;

    // Honeypot: only a bot can reach this field.
    if (form.website.trim()) {
      setStatus({
        state: "error",
        message: CONTACT_EMAIL
          ? `We couldn’t verify this submission. Please email ${CONTACT_EMAIL} instead.`
          : "We couldn’t verify this submission. Please try again."
      });
      return;
    }

    if (!form.name.trim() || !form.email.trim() || !form.details.trim()) {
      setStatus({ state: "error", message: "Please fill in your name, work email, and a short brief." });
      return;
    }

    // Time-on-form: instant posts are automated, not human.
    if (Date.now() - startedAt.current < MIN_FILL_MS) {
      setStatus({ state: "error", message: "That came through a little too fast — please submit once more." });
      return;
    }

    setStatus({ state: "submitting", message: "" });
    try {
      await submitContactInquiry(
        {
          fullName: form.name,
          workEmail: form.email,
          company: form.company,
          service: "Website inquiry",
          budget: form.budget,
          timeline: "",
          details: form.phone ? `${form.details}\n\nPhone: ${form.phone}` : form.details,
          website: form.website
        },
        ANTISPAM_TOKEN
      );
      setStatus({ state: "success", message: "Thanks — we’ve got your brief and will be in touch shortly." });
      setForm(EMPTY);
      startedAt.current = Date.now();
    } catch (err) {
      setStatus({
        state: "error",
        message: "We couldn’t send that just now. Please email us directly or try again in a moment."
      });
    }
  };

  return (
    /* id="faq" is load-bearing: analyticsAutoTrack resolves a click's source
       from the nearest section[id]. */
    <Section tone="olive" panel size="lg" id="faq" aria-labelledby={HEADING_ID}>
      <div className={styles.grid}>
        {/* LEFT — heading + the FAQ, ruled with hairlines rather than boxed */}
        <div className={styles.faqCol}>
          <div className={styles.head}>
            <MicroLabel as="p" className={`micro--accent ${styles.kicker}`}>
              {KICKER}
            </MicroLabel>
            {/* An h2 inside <main>: HeadingWipe owns its motion — no Reveal. */}
            <Statement as="h2" size="h2" id={HEADING_ID}>
              {HEADING.lead} <em>{HEADING.accent}</em>
              {HEADING.tail}
            </Statement>
            {FAQ_CONTENT.subtitle ? <p className={styles.lead}>{FAQ_CONTENT.subtitle}</p> : null}
          </div>

          <ul className={styles.faqList}>
            {FAQS.map((item, index) => {
              const isOpen = openFaq === index;
              const buttonId = `faq-q-${index}`;
              const panelId = `faq-a-${index}`;

              return (
                <Reveal
                  as="li"
                  key={item.q}
                  direction="up"
                  delay={Math.min(index, 3) * 60}
                  className={`${styles.faqItem} ${isOpen ? styles.faqOpen : ""}`}
                >
                  <button
                    type="button"
                    id={buttonId}
                    className={`focus-ring ${styles.faqQ}`}
                    aria-expanded={isOpen}
                    aria-controls={panelId}
                    onClick={() => setOpenFaq(isOpen ? -1 : index)}
                    data-analytics-interaction={`faq_open:${item.q}`}
                  >
                    <MicroLabel className={styles.faqIndex}>
                      {String(index + 1).padStart(2, "0")}
                    </MicroLabel>
                    <span className={styles.faqText}>{item.q}</span>
                    {/* State, not motion: the glyph swaps, nothing rotates. */}
                    <span className={styles.faqToggle}>
                      <Icon name={isOpen ? "minus" : "plus"} size={16} />
                    </span>
                  </button>

                  <div className={styles.faqPanel} id={panelId} role="region" aria-labelledby={buttonId}>
                    <div className={styles.faqPanelInner}>
                      <p className={styles.faqAnswer}>{item.a}</p>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </ul>

          <div className={styles.faqFoot}>
            <MicroLabel as="p" className={styles.faqFootText}>
              {DIRECT_PROMPT}
            </MicroLabel>
            <ul className={styles.faqFootList}>
              {WA_TALK_URL ? (
                <li>
                  {/* WCAG 2.5.3: the accessible name opens with the visible
                      label, then flags the new tab.
                      "support" is the icon set's reach-a-human glyph — the set
                      ships no messaging icon and inline SVG is not allowed. */}
                  <a
                    className={`focus-ring ${styles.faqFootLink}`}
                    href={WA_TALK_URL}
                    aria-label={`${WHATSAPP_LABEL}, opens in a new tab`}
                    {...EXTERNAL_LINK_PROPS}
                  >
                    <Icon name="support" size={18} />
                    <span className={styles.faqFootLabel}>{WHATSAPP_LABEL}</span>
                  </a>
                </li>
              ) : null}
              {CONTACT_EMAIL && MAIL_URL ? (
                <li>
                  <a className={`focus-ring ${styles.faqFootLink}`} href={MAIL_URL}>
                    <Icon name="mail" size={18} />
                    <span className={styles.faqFootLabel}>{CONTACT_EMAIL}</span>
                  </a>
                </li>
              ) : null}
              {PHONE_DISPLAY && TEL_URL ? (
                <li>
                  <a className={`focus-ring ${styles.faqFootLink}`} href={TEL_URL}>
                    <Icon name="phone" size={18} />
                    <span className={styles.faqFootLabel}>{PHONE_DISPLAY}</span>
                  </a>
                </li>
              ) : null}
            </ul>
            {WA_TALK_URL && RESPONSE_NOTE ? (
              <p className={styles.faqFootNote}>{RESPONSE_NOTE}</p>
            ) : null}
          </div>
        </div>

        {/* RIGHT — the lead form, a paper ticket on the olive panel. Reveal sits
            on the column; the TicketCard inside carries no transition. */}
        <Reveal className={styles.formCol} direction="up" delay={120}>
          <TicketCard tone="paper" className={styles.formCard}>
            <Statement as="h3" size="h3" className={styles.formTitle}>
              {FORM_COPY.titleLead} <em>{FORM_COPY.titleAccent}</em>
              {FORM_COPY.titleTail}
            </Statement>
            <p className={styles.formHelp}>{FORM_COPY.help}</p>

            <form className={styles.form} onSubmit={handleSubmit} noValidate>
              <div className={styles.fieldRow}>
                <label className={styles.field}>
                  <MicroLabel className={styles.fieldLabel}>{FIELDS.name}</MicroLabel>
                  <input type="text" autoComplete="name" value={form.name} onChange={update("name")} required />
                </label>
                <label className={styles.field}>
                  <MicroLabel className={styles.fieldLabel}>
                    {FIELDS.company} <span className={styles.fieldHint}>{FIELDS.optional}</span>
                  </MicroLabel>
                  <input
                    type="text"
                    autoComplete="organization"
                    value={form.company}
                    onChange={update("company")}
                  />
                </label>
              </div>

              <div className={styles.fieldRow}>
                <label className={styles.field}>
                  <MicroLabel className={styles.fieldLabel}>{FIELDS.email}</MicroLabel>
                  <input
                    type="email"
                    autoComplete="email"
                    placeholder={FIELDS.emailPlaceholder}
                    value={form.email}
                    onChange={update("email")}
                    required
                  />
                </label>
                <label className={styles.field}>
                  <MicroLabel className={styles.fieldLabel}>
                    {FIELDS.phone} <span className={styles.fieldHint}>{FIELDS.optional}</span>
                  </MicroLabel>
                  <input
                    type="tel"
                    autoComplete="tel"
                    placeholder={FIELDS.phonePlaceholder}
                    value={form.phone}
                    onChange={update("phone")}
                  />
                </label>
              </div>

              <label className={styles.field}>
                <MicroLabel className={styles.fieldLabel}>
                  {FIELDS.budget} <span className={styles.fieldHint}>{FIELDS.optional}</span>
                </MicroLabel>
                {/* The wrapper only positions the chevron; the <select> is still
                    the label's one labelable descendant. */}
                <span className={styles.selectWrap}>
                  <select value={form.budget} onChange={update("budget")}>
                    <option value="">{FIELDS.budgetPlaceholder}</option>
                    {BUDGETS.map((budget) => (
                      <option key={budget} value={budget}>
                        {budget}
                      </option>
                    ))}
                  </select>
                  <Icon name="chevron-down" size={18} className={styles.selectIcon} />
                </span>
              </label>

              <label className={styles.field}>
                <MicroLabel className={styles.fieldLabel}>{FIELDS.details}</MicroLabel>
                <textarea
                  rows={4}
                  placeholder={FIELDS.detailsPlaceholder}
                  value={form.details}
                  onChange={update("details")}
                  required
                />
              </label>

              {/* Honeypot — off-screen, untabbable and hidden from assistive tech.
                  A non-empty value here is treated as spam, client and server side. */}
              <div className={styles.honeypot} aria-hidden="true">
                <label>
                  Website
                  <input
                    type="text"
                    name="website"
                    tabIndex={-1}
                    autoComplete="off"
                    value={form.website}
                    onChange={update("website")}
                  />
                </label>
              </div>

              <div className={styles.actions}>
                <div className={styles.actionsRow}>
                  <button
                    type="submit"
                    className={`btn ${styles.submit}`}
                    disabled={status.state === "submitting"}
                  >
                    {status.state === "submitting" ? FORM_COPY.sending : FORM_COPY.submit}
                    <Icon name="arrow-right" size={18} />
                  </button>

                  {/* The genuine alternative to the form. Deliberately a
                      .link-arrow: this section spends its one lime pill on the
                      submit button. A <div>, not a <p>: the global `p a` rules
                      would re-weight the link's underline on hover. */}
                  {WA_BRIEF_URL ? (
                    <div className={styles.alt}>
                      <span className={styles.altOr}>{ALT_OR}</span>
                      <a
                        className={`link-arrow ${styles.altLink}`}
                        href={WA_BRIEF_URL}
                        aria-label={`${WHATSAPP_LABEL}, opens in a new tab`}
                        {...EXTERNAL_LINK_PROPS}
                      >
                        {WHATSAPP_LABEL}
                      </a>
                    </div>
                  ) : null}
                </div>

                {WA_BRIEF_URL && RESPONSE_NOTE ? (
                  <p className={styles.altNote}>{RESPONSE_NOTE}</p>
                ) : null}

                <MicroLabel as="p" className={styles.privacy}>
                  <Icon name="shield-check" size={16} />
                  <span>{FORM_COPY.privacy}</span>
                </MicroLabel>
              </div>

              {status.state === "success" ? (
                /* A lime fill carries ink only — data-surface flips the vars. */
                <p className={`${styles.notice} ${styles.noticeOk}`} role="status" data-surface="lime">
                  <Icon name="check" size={18} />
                  <span>{status.message}</span>
                </p>
              ) : null}
              {status.state === "error" ? (
                <p className={`${styles.notice} ${styles.noticeErr}`} role="alert">
                  {status.message}
                </p>
              ) : null}
            </form>
          </TicketCard>
        </Reveal>
      </div>
    </Section>
  );
}
